import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadDataLinkData, dataLinkHasDependencies } from './utils';
import { dataLinksLoadedAction } from '../../redux/actions/datalinks';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import {
  getDoc,
  getDocs,
  collection,
  getFirestore,
  query,
  onSnapshot,
  doc,
  where,
  getCountFromServer
} from 'firebase/firestore';
import { consoleLog } from '../../utils/Logger';
import useSquirrellyHelpers from './helpers';
import useParams from '../params';
import { keyUpdatedAction } from '../../redux/actions/local';

/*
    Hook which orchestrates everything for a page at runtime. This hook is not for use when we are desining a page

    Pages are edited from the /pages collection

    When a page is published it goes to either /pages_public or /pages_private and that is what this hook is working with.
      These two collections maintain a flat optimised document structure for the page with any subcollections as nested arrays

    - a page always ships around with a .dataLinks array of pageDataLinks of which each has a .dataLink property containing the underlying data link
    - we ship data links around in this way so we don't expose unncessary datalinks to unintended users
    - it loads the data from the underlying Data Link if it has not already been loaded
    - the hook sets { document, documents } on the pageDataLinks hook state where document is the selected document if there is one
    - we publish any page data links to redux here too

    The hook should be used liked so:

    const { html, ready, page, data } = usePage({ siteMapPage, isPublicPage }) 
    The page returned is 

    { ready } denotes that:
      - the data associated with the page data links has been loaded
      - the html has been prepared
      - the onload execution function has been executed if there is one

      i.e. the page is ready to be rendered

*/
const usePage = ({ siteMapPage, isPublicPage = false }) => {
  const dispatch = useDispatch();

  const params = useParams(siteMapPage?.path);

  useSquirrellyHelpers();

  /* we don't use useStoreSelector to populate dataLinks since they are populated from the page in the useEffect below */
  const { selections, documents, dataLinks, counts, parameters } = useSelector(
    state => state.DataLinks
  );
  const [firebaseUser] = useAuthState(auth);
  const local = useSelector(state => state.Local);
  const user = useSelector(state => state.User);
  const {
    processing,
    results,
    actionSequence: lastActionSequenceRan
  } = useSelector(state => state.ActionSequences);

  const [sitePage, setSitePage] = useState();
  const [pageData, setPageData] = useState({});

  const [ready, setReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [onLoadExecuted, setOnLoadExecuted] = useState(false);

  const [html, setHtml] = useState();

  consoleLog('usePage', {
    sitePage,
    isPublicPage
  });

  const loadHtml = async () => {
    const html = await getPageHtml();
    setHtml(html);

    if (sitePage.path !== pageData.local?.currentPage?.path) {
      dispatch(
        keyUpdatedAction('currentPage', {
          path: sitePage.path,
          id: sitePage.id,
          params: sitePage.params,
          publicAccess: sitePage.publicAccess,
          title: sitePage.title || ''
        })
      );
    }
  };

  /* prepare the html when the page is ready */
  useEffect(() => {
    if (dataLoaded) {
      loadHtml();
    }
  }, [dataLoaded, pageData, user, firebaseUser]);

  /* is the page ready to render */
  useEffect(() => {
    if (
      !ready &&
      dataLoaded &&
      html !== undefined &&
      sitePage !== undefined &&
      onLoadExecuted
    ) {
      setReady(true);
    }
  }, [dataLoaded, html, sitePage, onLoadExecuted]);

  useEffect(() => {
    if (sitePage) {
      dispatch(
        keyUpdatedAction('currentPage', {
          path: sitePage.path,
          id: sitePage.id,
          params: sitePage.params,
          publicAccess: sitePage.publicAccess
        })
      );
    }
  }, [params]);

  /* if there is not on load sequence but data is loaded, then mark all data as loaded */
  useEffect(() => {
    if (sitePage && dataLoaded) {
      consoleLog('usePage.useEffect.setOnLoadExecuted');
      if (
        !sitePage.onLoadSequenceId ||
        sitePage.onLoadSequenceId?.trim().length === 0
      ) {
        setOnLoadExecuted(true);
      }
    }
  }, [sitePage, dataLoaded]);

  const getPageRole = async page => {
    let pageTitle = page?.title || '';

    let html =
      page?.html ||
      '<div>No page content has been setup [code-auth-private]</div>';

    let metadata = page?.metadata || {};

    if (!page.publicAccess) {
      const roleSpecificDoc = doc(
        db,
        `pages_private/${page.id}/html_by_role`,
        user?.role
      );
      const roleSpecificDocSnap = await getDoc(roleSpecificDoc);
      if (roleSpecificDocSnap.exists()) {
        const htmlDoc = roleSpecificDocSnap.data();
        if (htmlDoc.html && htmlDoc.html.trim().length > 0) {
          html = htmlDoc.html;
        }
        if (htmlDoc.title && htmlDoc.title.trim().length > 0) {
          pageTitle = htmlDoc.title;
        }
        metadata = { ...(htmlDoc.metadata || {}), ...metadata };
      }
    }

    return { html, title: pageTitle, metadata };
  };

  const getFirestorePage = async () => {
    consoleLog('usePage.getFirestorePage');
    const docRef = doc(
      db,
      isPublicPage ? 'pages_public' : 'pages_private',
      siteMapPage.id
    );

    if (siteMapPage.listen) {
      // load page via a snapshot listener to subscribe to realtime updates
      return onSnapshot(docRef, async doc => {
        const docData = doc.data();
        const pageRole = await getPageRole(docData);
        setSitePage({ ...docData, ...pageRole, params });
      });
    }

    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const docData = docSnap.data();
      const pageRole = await getPageRole(docData);
      setSitePage({ ...docData, ...pageRole, params });
    }

    return () => {}; // for useEffect unsub
  };

  /* get the underlying document from the published page */
  useEffect(() => {
    consoleLog('usePage.useEffect.siteMapPage changed');
    if (params) {
      if ((siteMapPage && !sitePage) || siteMapPage.id !== sitePage.id) {
        setOnLoadExecuted(false);
        getFirestorePage();
      }
    }
  }, [siteMapPage, params]);

  /* 
    watch for the completion of the any on-load page sequence being finished 
      and then load any datalinks whose loading behaviour is set to execute after the sequence
  */
  useEffect(() => {
    if (
      sitePage &&
      !processing &&
      lastActionSequenceRan?.id === sitePage?.onLoadSequenceId
    ) {
      loadPageDataLinks({ loadingBehaviour: 'after_onload_sequence' });
      setOnLoadExecuted(true);
    }
  }, [processing, sitePage, lastActionSequenceRan]);

  /* if everything is ready and there is an onload action sequence, then execute it */
  useEffect(() => {
    async function processOnLoad() {
      if (
        dataLoaded &&
        !onLoadExecuted &&
        sitePage?.onLoadSequenceId &&
        sitePage?.onLoadSequenceId?.trim().length > 0
      ) {
        consoleLog('usePage.useEffect.processOnLoadSequence.execute');
        await window.actionSequencer.execute({
          actionSequenceId: sitePage.onLoadSequenceId,
          page: sitePage
        });
        setOnLoadExecuted(true);
      }
    }
    consoleLog('usePage.useEffect.processOnLoadSequence.pre');
    processOnLoad();
  }, [pageData]);

  /* prepare the data for the page */
  useEffect(() => {
    consoleLog('usePage.useEffect.page data');
    if (!sitePage) {
      return;
    }

    let data = {
      user,
      local,
      sequencer: {
        processing,
        results
      }
    }; // the data that squirelly will use on the page

    async function getData() {
      const updatedPageDataLinks = [...sitePage.dataLinks];

      updatedPageDataLinks.forEach(pageDataLink => {
        data[pageDataLink.dataLinkNameSingular.toLowerCase()] = {};
        data[pageDataLink.dataLinkNamePlural.toLowerCase()] =
          documents[pageDataLink.dataLinkId]?.documents || [];

        if (selections[pageDataLink.dataLinkId]?.documentId) {
          data[pageDataLink.dataLinkNameSingular.toLowerCase()] = documents[
            pageDataLink.dataLinkId
          ].documents.find(
            d => d.id === selections[pageDataLink.dataLinkId].documentId
          );
        }

        if (counts[pageDataLink.dataLinkId] !== undefined) {
          data[`${pageDataLink.dataLinkNamePlural.toLowerCase()}_count`] =
            counts[pageDataLink.dataLinkId];
        }
      });

      // run any onload action sequences
      await Promise.all(
        updatedPageDataLinks.map(async pageDataLink => {
          if (pageDataLink.runActionSequence) {
            await window.actionSequencer.execute({
              actionSequenceId: pageDataLink.actionSequenceId,
              page: sitePage,
              data
            });
          }
        })
      );

      try {
        if (sitePage.dataLinksFunction) {
          // eslint-disable-next-line
          const AsyncFunction = Object.getPrototypeOf(async function() {})
            .constructor;

          const modifyDataFunction = new AsyncFunction(
            'data',
            'countDocuments',
            'user',
            'local',
            'getDocument',
            'getDocuments',
            sitePage.dataLinksFunction
          );

          const getDocument = async (path, id, databaseId) => {
            const db = getFirestore(databaseId);
            const docRef = doc(db, path, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              return docSnap.data();
            }
          };

          const getDocuments = async (path, databaseId) => {
            const db = getFirestore(databaseId);
            const querySnapshot = await getDocs(query(collection(db, path)));
            const docs = [];
            querySnapshot.forEach(doc => docs.push(doc.data()));
            return docs.filter(d => !d.deleted);
          };

          const countDocuments = async ({
            databaseId = '',
            path,
            filters = []
          }) => {
            let countQuery;

            const queryParts = filters.map(filter => {
              return where(filter.field, filter.operator, filter.value);
            });

            const countDb = getFirestore(databaseId);

            if (queryParts.length > 0) {
              countQuery = query(collection(countDb, path), ...queryParts);
            } else {
              countQuery = query(collection(countDb, path));
            }

            const itemCountSnap = await getCountFromServer(countQuery);
            return itemCountSnap.data().count;
          };

          data = await modifyDataFunction(
            data,
            countDocuments,
            user,
            local,
            getDocument,
            getDocuments
          );
        }
      } catch (error) {
        console.error('page dataLinksFunction could not be executed', error);
      }

      setPageData({ ...(data || {}), params });
      setDataLoaded(true);
    }

    if (sitePage?.dataLinks) {
      getData();
    } else {
      setPageData({ ...(data || {}), params });
    }
  }, [
    documents,
    selections,
    counts,
    dataLinks,
    local,
    processing,
    results,
    sitePage
  ]);

  /* populates data links with data on-demand */
  const loadPageDataLinks = useCallback(
    async ({ loadingBehaviour = '' }) => {
      consoleLog('usePage.useEffect.loadPageDataLinks.exec');

      if (sitePage.dataLinks?.length > 0 && loadingBehaviour === '') {
        // make sure the data links associated with this page are made available in redux
        const dataLinksOnPage = sitePage.dataLinks?.map(p => ({
          ...p.dataLink
        }));
        dispatch(dataLinksLoadedAction(dataLinksOnPage));

        sitePage.dataLinks.forEach(pageDataLink => {
          pageDataLink.dataLinkNameSingular = pageDataLink.dataLinkNameSingular.toLowerCase();
          pageDataLink.dataLinkNamePlural = pageDataLink.dataLinkNamePlural.toLowerCase();
        });
      }

      if (sitePage.dataLinks?.length === 0) {
        if (loadingBehaviour === '') {
          setPageData({
            user,
            local,
            params
          });
          setDataLoaded(true);
        }
        return;
      }

      await Promise.all(
        sitePage.dataLinks
          .filter(
            d =>
              (d.dataLink.loadingBehaviour === undefined &&
                loadingBehaviour === '') ||
              d.dataLink.loadingBehaviour === loadingBehaviour
          )
          .map(async pageDataLink => {
            if (
              documents[pageDataLink.dataLinkId]?.snapshotActive ||
              !pageDataLink.dataLink
            ) {
              return;
            }
            /* 
            do not try and load data link documents for data links that depend on document selections from other data links, or placeholders
          */
            if (
              loadingBehaviour === '' &&
              dataLinkHasDependencies(pageDataLink.dataLink)
            ) {
              return;
            }

            await loadDataLinkData({
              dataLink: pageDataLink.dataLink,
              selections,
              user,
              local,
              firebaseUser,
              documents,
              dispatch,
              dataLinks,
              data: pageData,
              params,
              autoSelection: {
                params,
                autoSelectFirstDocument: pageDataLink.autoSelectFirstDocument,
                autoSelectDocument: pageDataLink.autoSelectDocument,
                autoSelectDocumentParameterNames:
                  pageDataLink.autoSelectDocumentParameterNames,
                autoSelectDocumentFieldNames:
                  pageDataLink.autoSelectDocumentFieldNames
              }
            });
          })
      );
    },
    [selections, local, firebaseUser, user, documents, sitePage]
  );

  /* load the data associated with each page data link if there is not already a redux snapshot active */
  useEffect(() => {
    consoleLog('usePage.useEffect.loadPageDataLinks.check');
    let unsubPageDataLinks = () => {};

    if (sitePage) {
      loadPageDataLinks({ loadingBehaviour: '' });
    }

    return () => {
      unsubPageDataLinks();
    };
  }, [sitePage]);

  /* 
    whenever a document is selected we need to reload any data link documents that rely on a selection
      or whenever local or user changes since these can also exist in placeholders in datalinks and path segments
   */
  useEffect(() => {
    consoleLog('usePage.useEffect.loadDependentDataLinks.check');
    async function loadDependentDataLinks() {
      consoleLog('usePage.useEffect.loadDependentDataLinks.exec');
      await Promise.all(
        sitePage.dataLinks
          .filter(p => dataLinkHasDependencies(p.dataLink))
          .filter(
            p =>
              !(documents[p.dataLinkId]?.snapshotActive && p.dataLink.loadOnce)
          )
          .map(async pageDataLink => {
            /* only proceed to load the dependent data link if all data link segments have been loaded */
            const dependentDataLinks = pageDataLink.dataLink.pathsegments.filter(
              p => p.type === 'datalink'
            );
            if (
              dependentDataLinks.some(d => selections[d.datalink] === undefined)
            ) {
              return;
            }
            consoleLog(
              'usePage.useEffect.loadDependentDataLinks.loading',
              pageDataLink.dataLink
            );
            await loadDataLinkData({
              parameters,
              dataLink: pageDataLink.dataLink,
              selections,
              user,
              firebaseUser,
              documents,
              local,
              data: pageData,
              params,
              dataLinks,
              dispatch,
              autoSelection: {
                params,
                autoSelectFirstDocument: pageDataLink.autoSelectFirstDocument,
                autoSelectDocument: pageDataLink.autoSelectDocument,
                autoSelectDocumentParameterNames:
                  pageDataLink.autoSelectDocumentParameterNames,
                autoSelectDocumentFieldNames:
                  pageDataLink.autoSelectDocumentFieldNames
              }
            });
          })
      );
    }
    if (sitePage?.dataLinks?.length > 0) {
      loadDependentDataLinks();
    }
  }, [selections, local, user, sitePage?.dataLinks]);

  const getPageHtml = async () => {
    let hasMatchedStated = false;
    let html = sitePage?.html || '';

    sitePage.views
      ?.filter(v => v.code?.trim().length > 1)
      .forEach(view => {
        if (!hasMatchedStated) {
          try {
            // eslint-disable-next-line
            const computeFunction = new Function(
              'local',
              'user',
              'firebaseUser',
              'dataLinks',
              'data',
              view.code
            );
            const value = computeFunction(
              pageData?.local || local,
              pageData?.user || user,
              firebaseUser,
              dataLinks,
              pageData || {}
            );

            if (value === true || value === 'true') {
              hasMatchedStated = true;
              html = view.appendToPage ? `${html}${view.html}` : view.html;
            }
          } catch (err) {
            console.log(
              `error in the page state/view function code (view: ${view.name} id: ${view.id}):`,
              err
            );
          }
        }
      });

    return html;
  };

  return {
    html,
    page: sitePage,
    data: pageData,
    ready
  };
};

export default usePage;
